import Backbone from 'backbone'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import mockSync from 'scoreboard/components/mock-sync'
import nestCollection from 'scoreboard/components/nest-collection'
import nestModel from 'scoreboard/components/nest-model'
import adjustBreakdownDatasetFields from '../edit/serie/adjust-breakdown-dataset-fields'
import breakdownLabel from '../breakdown-label'
import ChartDesignerSortSettings from './chart-designer-sort-settings'
import ChartDesignerSerie from './chart-designer-serie'
import featureMetadata from '../edit/feature-metadata'
import ChartDesignerSeriePeriod from './chart-designer-serie-period'
import ChartDesignerBreakdown from './chart-designer-breakdown'
import ChartDesignerFeature from './chart-designer-feature'
const { idPredicate, assignNewIds } = ChartDesignerFeature
import ChartData from 'scoreboard/charts/chart-data/chart-data'
import findByIdsFactory, {matchesFactory} from './find-by-ids-factory'
import permissions from 'scoreboard/components/permissions'
import Dataset from 'scoreboard/datasets/model/dataset'
import unwrapModel from 'scoreboard/components/unwrap-model'
import enums from 'scoreboard/components/enums'
import constants from 'scoreboard/components/constants'
import { axisDataTypeInfo } from 'scoreboard/dashboards/widgets/chart/config/y-axis-range-config'
import DataType from 'scoreboard/datasets/model/data-type'
const { POSTAL_CODE, NUMBER, PERCENT, CURRENCY } = DataType
import moveDecimal from 'scoreboard/components/move-decimal'

const findByIds = findByIdsFactory()
const findByUnderlyingFeatureIds = findByIdsFactory({
	idAttr: 'underlyingFeatureId',
	tempIdAttr: 'underlyingFeatureTempId'
})
const matchesBySerieSourceIds = matchesFactory({
	idAttr: 'serieSourceId',
	tempIdAttr: 'serieSourceTempId'
})
import { getPlotTypeLabelConf } from '../edit/label-settings'
import updateLineDataSource from '../edit/analytics/update-line-data-source'
import ThresholdColorModel from 'scoreboard/components/threshold-colors/threshold-colors-model'

/**
 * @typedef {Object} ChartDesigner
 *
 * @property features
 */
const ChartDesigner = Backbone.Model.extend({

	/** @type {ChartData} */
	data: null,

	/**
	 * @constructs
	 *
	 * @param {Object} [opt]
	 *
	 * @param {ChartData} [opt.data] - the ChartData for the designer; includes
	 * the data used to render the chart and the domain objects related to it
	 *
	 * @param {String} attr.chartLayout - layout to enforce for the chart
	 */
	initialize: function (attr, opt = {}) {
		this.data = opt.data || new ChartData()
		if (!this.attributes.id && !this.attributes.tempId) {
			this.attributes.tempId = tempChartDesignerId()
		}

		this.features = new FeatureCollection(this.get('features'))
		nestCollection({
			parent: this,
			parentAttribute: 'features',
			children: this.features
		})

		/**
		 * @type ChartDesignerBreakdown[]
		 */
		this.breakdowns = new BreakdownCollection(this.get('breakdowns'), { data: this.data })
		nestCollection({
			parent: this,
			parentAttribute: 'breakdowns',
			children: this.breakdowns
		})

		this.leftAxisBarSortSettings = new ChartDesignerSortSettings(this.get('leftAxisBarSortSettings'))
		nestModel({
			parent: this,
			parentAttribute: 'leftAxisBarSortSettings',
			child: this.leftAxisBarSortSettings
		})

		this.rightAxisBarSortSettings = new ChartDesignerSortSettings(this.get('rightAxisBarSortSettings'))
		nestModel({
			parent: this,
			parentAttribute: 'rightAxisBarSortSettings',
			child: this.rightAxisBarSortSettings
		})

		//whenever the series is reorder, ensure the sort order property on each
		//serie is right
		this.features.on('sort', (collection, opts) => {
			if (_.isNil(collection)) {
				collection = this.features
			}
			let newSortOrder
			let currentAxis
			_.each(collection.models, model => {
				let axis = model.get('axis')
				if (currentAxis !== axis) {
					newSortOrder = 0
					currentAxis = axis
				}
				model.set('sortOrder', newSortOrder++)
			})
		})
	},

	/**
	 * Returns the sort settings for bars on the given axis
	 *
	 * @param {ChartAxis} axis
	 *
	 * @returns {ChartDesignerSortSettings}
	 */
	barSortSettings: function ({ axis }) {
		return this[axis + 'AxisBarSortSettings']
	},

	/**
	 * The label for the feature (e.g. what is displayed in the inactive axis panel)
	 */
	featureLabel: function ({ feature, id, tempId }) {

		if (_.isNil(feature)) {
			feature = this.findFeatureById({ id, tempId })
		}
		return feature.getLabel({data: this.data})
	},

	findFeatureById: function ({ id, tempId }) {
		let allFeatures = this.getFeatures()
		if (id) {
			return _.find(allFeatures, f => f.get('id') === id)
		} else if (tempId) {
			return _.find(allFeatures, f => f.get('tempId') === tempId)
		}
	},

	/**
	 * Given a ChartDataSerie, return its corresponding ChartDesignerFeature
	 *
	 * @param {ChartDataSerie}
	 *
	 * @returns {ChartDesignerFeature}
	 */
	findFeatureByUnderlyingSerieId: function (dataSerie) {
		return this.findFeatureById({
			id: dataSerie.underlyingSerieId,
			tempId: dataSerie.underlyingSerieTempId
		})
	},

	featuresByAxis: function (axis) {
		return this.features.ordered().filter(f => f.get('axis') === axis)
	},

	breakdownLabel: function (breakdown) {
		return breakdownLabel(_.extend({ domainData: this.data },
			_.invoke(breakdown, 'toJSON')))
	},

	/**
	 * Finds the breakdown range matching either the given id or tempId
	 *
	 * @param {Number} id - a persisted range's ID
	 * @param {String} tempId - a non-persisted range's tempId
	 *
	 * @returns {ChartDesignerRange}
	 */
	breakdownRange: function ({ id, tempId }) {
		return _(this.breakdowns.toArray())
			.flatMap(b => b.ranges.toArray())
			.find(r => {
				if (!_.isNil(id)) {
					return r.id == id
				} else if (!_.isNil(tempId)) {
					return r.get('tempId') == tempId
				}
			})
	},

	/**
	 * Get the grouping calendar ID for the given serie period
	 * @param {ChartDesignerSeriePeriod} period
	 * @return {number}
	 */
	periodGroupCalendarId: function(period) {
		return this._breakdownRangeForPeriod(period).get('groupCalendarId')
	},

	/**
	 * Get the breakdown calendar ID for the given serie period
	 * @param {ChartDesignerSeriePeriod} period
	 * @return {number}
	 */
	periodBreakdownCalendarId: function(period) {
		const breakdownRangeForPeriod = this._breakdownRangeForPeriod(period)
		return _.invoke(breakdownRangeForPeriod, 'get', 'groupCalendarId')
	},

	/**
	 * @param {ChartDesignerSeriePeriod} period
	 * @private
	 */
	_breakdownRangeForPeriod: function(period) {
		const safePeriod = unwrapModel(period)
		return this.breakdownRange({
			id: safePeriod.breakdownRangeId,
			tempId: safePeriod.breakdownRangeTempId
		})
	},

	setFeatureColor: function (featureModel, color) {
		this.updateDataSeries(featureModel, f => {
			f.color = color
			if (f.forecast) {
				f.forecast.color = f.forecast.fillColor = color
			}

			const colorSettings = this.get('colors').find(s => s.plotType === f.type)
			// #40011 Don't change point colors for score coloring as those are
			// set separately
			if (!colorSettings || colorSettings.colorType !== 'score') {
				_.forEach(f.points, p => p.color = color)
			}
		})

		let parentFeature = findByIds({
			id: featureModel.get('parentId'),
			tempId: featureModel.get('tempParentId'),
			collection: this.features
		})

		if (parentFeature) {
			// If the updated feature is a child of another feature, then its
			// color might be represented in the parent's 'fills' or 'lines'
			// arrays
			this.updateDataSeries(parentFeature, f => {
				function applyColor (collectionAttr) {
					let fillOrLine = findByUnderlyingFeatureIds(_.extend(
						featureModel.pick('id', 'tempId'),
						{ collection: f[collectionAttr] }))

					if (fillOrLine) {
						fillOrLine.color = color
					}
				}

				applyColor('fills')
				applyColor('lines')

				// For regressions/trend lines, the color is set on the child
				// line, but the actual color ends up on the parent data serie
				// that's sent to the chart module, so update the color there
				if (f.type === 'regression') {
					f.color = color
				}
			})
		}

		featureModel.set('color', color)
	},

	setSeriePlotType: function (serieModel, plotType) {
		this.updateDataSeries(serieModel, f => f.type = plotType)
		const oldPlotType = serieModel.get('plotType')
		this.setGlobalSettingsPlotType(oldPlotType, plotType)
		serieModel.set('plotType', plotType)
		this.updateGlobalSettings()
	},

	/**
	 * #39946 If there is only 1 serie of a plot type, and that serie's plot type
	 * changes, this function will simply update the global settings for this plot
	 * type IF there isn't already an existing serie with the new plot type
	 */
	setGlobalSettingsPlotType(oldPlotType, newPlotType) {
		const plotTypeFeatures = _.filter(this.get('features'), f => f.plotType === oldPlotType)
		const existingNewPlotType = _.find(this.get('features'), f => f.plotType === newPlotType)
		if (plotTypeFeatures.length === 1 && !existingNewPlotType) {
			const updateSettingPlotType = setting => {
				const settings = this.get(setting)
				const settingForPlotType = _.find(settings, s => s.plotType === oldPlotType)
				if (settingForPlotType) {
					settingForPlotType.plotType = newPlotType
					this.set(setting, settings)
				}
			}

			updateSettingPlotType('colors')
			// #59478 include bar so that more labels aren't added when switching
			if (!['polar', 'line', 'area', 'bar'].includes(newPlotType)) {
				// #40183 don't show label options for these plot types
				updateSettingPlotType('labels')
			}
		}
	},

	/**
	 * Ensures that the global settings have all the correct defaults for all
	 * plot types in use. NOTE this is more or less a copy of
	 * ChartDesignerDao#fillAllMissingSettings. We do this again here so we don't
	 * need to make a server call whenever a serie plot type changes, so any changes
	 * here should be reflected there
	 */
	updateGlobalSettings: function() {
		this.set('colors', this.getMissingSettings(this.get('colors'),
			(id, chartDesignerId, plotType) => {
				return { id, chartDesignerId, plotType, colorType: 'manual' }
			}, [ 'tree-map' ]))

		this.set('labels', this.getMissingSettings(this.get('labels'),
			(id, chartDesignerId, plotType) => {
				const conf = getPlotTypeLabelConf(plotType)
				const isNotBar = plotType != 'bar'
				return { id, chartDesignerId, plotType, seriesLabels: isNotBar,
					chartedValues: conf.chartedValues && isNotBar,
					stageNumbers: conf.stageNumbers,
					abbreviateChartedValues: conf.abbreviateChartedValues, // #41299
					percentsRemaining: conf.percentsRemaining }
			}, [ 'polar', 'line', 'area', 'tree-map',
				'filled-map', 'spike-map', 'bubble-map']))
	},

	/**
	 * Generate a default setting object for any plot types that don't have them
	 * already
	 */
	getMissingSettings: function(settings, settingCreator, excludedPlotTypes) {
		const usedPlotTypes = _.concat(_.map(settings, s => s.plotType), excludedPlotTypes || [])
		const seriesWithoutSetting = _.filter(this.get('features'), f => f.type === 'serie' && !usedPlotTypes.includes(f.plotType))

		const results = _.clone(settings)
		// Get a list of existing negative ids, then find the minimum as a starting point
		// for generating new ids
		const minId = _.min(results.map(r => r.id))
		let lastTempId = minId > 0 ? 0 : minId
		const chartDesignerId = this.get('id')
		_.forEach(seriesWithoutSetting, s => {
			results.push(settingCreator(--lastTempId, chartDesignerId, s.plotType))
		})

		return results
	},

	/**
	 * Updates all series in this.data that match the given serie model. This lets
	 * us update the data that is normally built on the server and sent to the
	 * chart code, sparing us a server round-trip for simple updates (like
	 * serie color).
	 *
	 * @param {ChartDesignerFeature} feature
	 * @param {Function} updater - callback that updates the given data serie
	 */
	updateDataSeries: function (feature, updater) {
		_(this.data.plots())
			.flatMap(plot => plot.series)
			.filter(dataSerie => {
				let idToUse = feature.get('tempId') || feature.get('id')
				return dataSerie.id == idToUse
			})
			.forEach(updater)
	},

	/**
	 * Applies appropriate sortOrders after changing the order of the series.
	 *
	 * @param {DOMNode} sourcePackage - the node that we are moving
	 *
	 * @param {DOMNode} destination - the node that we are dropping the feature at
	 *
	 * @param {ChartDesignerFeature[]} prevFeatures - the list of features from the axis that the feature is coming from
	 *
	 * @param {ChartDesignerFeature[]} newFeatures - the list of features from the axis that the feature is going to
	 *
	 * @param {Number} newIndex - the new index of the node moved
	 *
	 * @param {Number} prevIndex - the moved node's previous index
	 *
	 */
	reorderFeatures: function({ sourcePackage, destination, prevFeatures,
		newFeatures, prevIndex, newIndex }) {
		let model = sourcePackage.featureModel
		this.removeFeature(model, sourcePackage.parentFeatureModel,
			prevFeatures, prevIndex)
		this.addFeature(model, destination.parentFeatureModel,
			newFeatures, newIndex)

		this.sortFeatures(model.get('axis'), newFeatures)
	},

	sortFeatures: function (axis, newFeatures) {
		//set the new order of the feature models
		let otherAxis = axis === 'right' ? 'left' : 'right'
		let otherFeatures = this.featuresByAxis(otherAxis)
		let featureModelOrder = axis === 'left' ? [].concat(newFeatures, otherFeatures) :
			[].concat(otherFeatures, newFeatures)
		this.features.set(featureModelOrder, {merge: true})

		// this is necessary in case the overall order of the axes didn't change
		this.features.trigger('sort')
		this.removeInvalidBreakdowns()
	},

	/**
	 * Removes a feature from the chart or a feature on the chart.
	 *
	 * @param {ChartDesignerFeature} item - the feature we are removing
	 *
	 * @param {ChartDesignerFeature} parent - the current parent of the
	 * 	feature, if applicable
	 *
	 * @param {ChartDesignerFeature[]} chartFeatures - the list of features
	 *  on the axis that the feature will be removed from
	 *
	 * @param {Number} prevIndex - the index that the feature needs to be removed at
	 */
	removeFeature: function(item, parent, chartFeatures, prevIndex) {
		// remove the node from either the chart or its previous parent
		if (_.isNil(parent)) {
			chartFeatures.splice(prevIndex, 1)
		} else {
			parent.removeChild(item)
		}
	},

	/**
	 * Adds a feature to the chart or a feature on the chart.
	 *
	 * @param {ChartDesignerFeature} item - the feature we are adding
	 *
	 * @param {ChartDesignerFeature} parent - the new parent of the
	 * 	feature, if applicable
	 *
	 * @param {ChartDesignerFeature[]} chartFeatures - the list of features
	 *  on the axis that the feature will be added to
	 *
	 * @param {Number} newIndex - the index that the feature needs to be added at
	 */
	addFeature: function(item, parent, chartFeatures, newIndex) {
		// add the node to the chart or its new parent
		if (_.isNil(parent)) {
			chartFeatures.splice(newIndex, 0, item)
		} else {
			parent.addChild(item, newIndex)
		}
	},

	addCopy: function(toCopy, modelConstructor) {
		const copy = _.cloneDeep(unwrapModel(toCopy))
		// Delete ids and set a temp so the server can populate new ones
		assignNewIds(copy, null)

		const copyModel = new modelConstructor(copy)
		const axisFeatures = this.featuresByAxis(copy.axis)

		// #53415 add the feature underneath the parent
		let parent = null
		const { parentId, tempParentId } = copyModel.attributes
		if (!_.isNil(parentId ?? tempParentId)) {
			parent = this.findFeatureById({ id: parentId, tempId: tempParentId })
		}

		this.addFeature(copyModel, parent, axisFeatures, 0)
		this.sortFeatures(copy.axis, axisFeatures)
	},

	/**
	 * Given an updated copy of a calendar period breakdown range, applies the
	 * changes to this ChartDesigner, including any adjustments that are needed
	 * as a result (like adjusting series.periods)
	 *
	 * @param {ChartDesignerRange} updatedRange
	 */
	updateCalendarPeriodBreakdownRange: function ({ updatedBreakdown, updatedRange }) {
		let oldRange = this.breakdownRange(updatedRange.pick('id', 'tempId'))

		let oldBreakdown = this.breakdowns.findByIds(updatedBreakdown.attributes)
		let oldBreakdownBy = oldBreakdown.get('breakdownBy')
		let newBreakdownBy = updatedBreakdown.get('breakdownBy')

		if (oldBreakdownBy != newBreakdownBy) {
			// If we're changing the breakdown's breakdownBy, we need to just
			// clear out all the old ranges and use the new one
			oldBreakdown.set('breakdownBy', newBreakdownBy)
			oldBreakdown.ranges.reset([updatedRange])

			// Any serie period references are now invalid
			this.features.forEach(f => _.invoke(f.periods, 'reset'))

			if (updatedBreakdown.isCalendarPeriodGroupBreakdown()) {
				// If we're changing TO grouped ranges, each serie will need
				// a new period reference to the new range
				this.initializeSeriePeriods(updatedRange)
			}
		} else if (updatedBreakdown.isCalendarPeriodStandardBreakdown()) {
			// If it's still a standard range, just apply the updated attributes
			// to the existing range (or add it if new)
			if (oldRange) {
				oldRange.set(updatedRange.toJSON())
			} else {
				oldBreakdown.ranges.push(updatedRange)
			}
		} else if (updatedBreakdown.isCalendarPeriodGroupBreakdown()) {
			// Grouped breakdown ranges have some more complex logic
			if (oldRange) {
				this.updateGroupedBreakdownRange({oldBreakdown, updatedRange})
			} else {
				this.addGroupedBreakdownRange({ breakdown: oldBreakdown, newRange: updatedRange })
			}
		}

		oldBreakdown.set('multipleCharts', updatedBreakdown.get('multipleCharts'))
	},

	updateGroupedBreakdownRange: function ({ oldBreakdown, updatedRange }) {
		let rangeIds = updatedRange.pick('id', 'tempId')
		let oldRange = this.breakdownRange(rangeIds)

		// If the group calendar changed, we need to change any absolute period
		// references in the series.
		// Note that these calendars could be null if we're using something like
		// DAY_OF_THE_WEEK instead of "real" defined calendars
		let oldGroupCal = calendars.calendarsById[oldRange.get('groupCalendarId')]
		let newGroupCal = calendars.calendarsById[updatedRange.get('groupCalendarId')]

		// The code below adjusts ChartDesignerFeature.periods, which is only
		// applicable for scorecard and initiative sources (datasest sources use
		// dataset filters instead)
		if ((this.hasScorecardItemSources() || this.hasInitiativeSources())
			&& oldGroupCal?.id != newGroupCal?.id) {

			let absolutePeriods = this.features
				// Find the ChartDesignerSeriePeriod that references the updated range
				// for each series
				.map(s => s.periods.findByRangeIds(rangeIds))
				// Only worry about absolute periods
				.filter(p => p && !_.isNil(p.get('periodId')))

			if (absolutePeriods.length > 0) {
				// Keep the same distance between the periods, but the baseline
				// is the latest absolute period converted to the new group calendar
				let oldLatestPeriodId = latestPeriod(absolutePeriods).id
				let newLatestPeriodId = calendars.correspondingPeriod(
					newGroupCal.id, oldLatestPeriodId).id

				absolutePeriods.forEach(periodDef => {
					let oldPeriodId = periodDef.get('periodId')
					let distance = calendars.distance(oldLatestPeriodId, oldPeriodId)

					periodDef.set('periodId',
						calendars.safeRelativePeriod(newLatestPeriodId, distance).id)
				})
			}
		}

		oldRange.set(updatedRange.attributes)
	},

	addGroupedBreakdownRange: function ({ breakdown, newRange }) {
		breakdown.ranges.add(newRange)

		// For a new grouped range, we need to go through all the series and
		// add a ChartDesignerSeriePeriod pointing to the new range
		this.getSerieFeatures().forEach(serie => {
			if (serie.domainType() === 'dataset') {
				// datasets use filters instead of ChartDesignerSeriePeriod
				return
			}

			// Just default to the same settings as the last period definition;
			// it seems likely the user will want those (or at least something similar)
			let sourcePeriod = serie.periods.last()
			let newPeriod = sourcePeriod
				? sourcePeriod.clone()
				: new ChartDesignerSeriePeriod({periodDelta: 0})

			newPeriod.set({
				breakdownRangeId: newRange.get('id'),
				breakdownRangeTempId: newRange.get('tempId')
			})

			if (!_.isNil(newPeriod.get('periodId'))) {
				// sourcePeriod's periodId may be in a different calendar than
				// our new range, so map it to the new range's group calendar
				let remappedPeriod = calendars.correspondingPeriod(
					newRange.get('groupCalendarId'), newPeriod.get('periodId'))
				newPeriod.set('periodId', remappedPeriod.id)
			}

			serie.periods.push(newPeriod)
		})
	},

	/**
	 * Invoked when a `Delete` menu option is selected.
	 * @see bootslide-edit-menu.js#DeleteOption
	 */
	deleteFeature: function (feature) {
		const {id, tempId, parentId, tempParentId} = unwrapModel(feature)

		let featureCollection = _.isNil(parentId) && _.isNil(tempParentId) ?
			this.features :
			this.features.find(idPredicate(parentId, tempParentId)).children // for stacks

		// function arg could be a representation of a feature, we must find the
		// one in the collection that matches based on it's id (#40972)
		const actualFeature = featureCollection.find(idPredicate(id, tempId))

		// #43037 The deleted feature could be a data source for a line (eg. a child
		// of a lines-and-bands or trend-line feature). If so, set the data source
		// for that line to "All" instead by clearing out the sourceSourceId
		this.features.featuresAndChildren()
			.filter(f => f.get('type') === 'line')
			.filter(f => {
				return matchesBySerieSourceIds({
					id: actualFeature.get('id'),
					tempId: actualFeature.get('tempId'),
					object: f
				})
			})
			.forEach(f => {
				updateLineDataSource(f, {}, { })
			})

		const oldAxisInfo = axisDataTypeInfo(this, this.data)
		featureCollection.remove(actualFeature)
		this.trigger('feature:removed')

		// If removing the feature left us without any scorecard/initiative
		// series, then the 'score' color option is no longer valid
		let scoreColorsAllowed = this.hasScorecardItemSources() || this.hasInitiativeSources()
		let colorSettingChanged = false
		let newColorSettings = _.cloneDeep(this.get('colors'))
		_.forEach(newColorSettings, colorSetting => {
			if (colorSetting.colorType === 'score' && !scoreColorsAllowed) {
				colorSetting.colorType = 'manual'
				colorSettingChanged = true
			}
		})

		if (colorSettingChanged) {
			this.set('colors', newColorSettings)
		}

		// #40450 make sure any breakdowns that were based on this serie are removed
		this.removeInvalidBreakdowns()

		const newAxisInfo = axisDataTypeInfo(this, this.data)
		adjustYAxisSettings(oldAxisInfo, newAxisInfo, this)

		// #49448 after deleting a feature, triggering the 'sort' event will
		// apply new sort orders to the features
		this.features.trigger('sort')

		adjustBreakdownDatasetFields(this)
	},

	setFeatureAxis (feature, newAxis) {
		let oldAxisInfo = axisDataTypeInfo(this, this.data)
		feature.set('axis', newAxis)
		// #41068 children should always have the same axis as the parent
		feature.children.forEach(c => c.set('axis', newAxis))

		let newAxisInfo = axisDataTypeInfo(this, this.data)
		// #53783 the axis min/max may need to be adjusted to account for a
		// change in the axis's dataType
		adjustYAxisSettings(oldAxisInfo, newAxisInfo, this)
	},

	getAllDatasetIds: function() {
		return _.uniq(this.getFeatures({ type: 'serie' })
			.map(s => s.get('source'))
			.filter(s => s.domainType === 'dataset')
			.flatMap(s => this.getAllDatasetIdsFromDataSource(s)))
	},

	getAllDatasetIdsFromDataSource: function(source) {
		// If it isn't a dataset source just return an empty list
		if (!source || source.domainType != 'dataset') return []

		let ids = []
		function pushIdsFromAggregation (aggregation) {
			if (!aggregation) {
				return
			}

			if (aggregation.datasetId) {
				ids.push(aggregation.datasetId)
			}

			if (aggregation.dataSourceType != 'calculated' && aggregation.startingDatasetId) {
				ids.push(aggregation.startingDatasetId)
			}
		}

		let aggregation = source.aggregation
		pushIdsFromAggregation(aggregation)

		_.forEach(aggregation.aggregations, agg => pushIdsFromAggregation(agg))

		return ids
	},

	/**
	 * Finds and removes any 'every-value' breakdowns that reference datasets not
	 * in use anymore. For calendar period breakdowns, any references to the old
	 * datasets are removed
	 */
	removeInvalidBreakdowns: function() {
		const datasetIds = this.getAllDatasetIds()
		const toRemove = []
		this.breakdowns.forEach(b => {
			const { breakdownBy, datasetId } = b.attributes
			if (b.isCalendarPeriodBreakdown()) {
				if (datasetId && !datasetIds.includes(datasetId)) {
					// Remove the dataset and field id if no series reference the dataset
					// anymore
					b.unset('datasetId')
					b.unset('datasetFieldId')
				}
			} else if (breakdownBy === 'every-value' && !datasetIds.includes(datasetId)) {
				toRemove.push(b)
			}
		})

		_.forEach(toRemove, b => this.breakdowns.remove(b))

		this.removeInvalidForecastOptions()
	},

	/**
	 * #42395 remove any forecasting options on series if we don't have any
	 * calendar breakdowns
	 */
	removeInvalidForecastOptions() {
		const hasCalendarBreakdown = _.some(this.breakdowns.toArray(),
			b => b.isCalendarPeriodBreakdown())

		if (hasCalendarBreakdown) return

		// We don't have a breakdown, so forecasting doesn't make sense
		_.forEach(this.getSerieFeatures(), s => {
			s.unset('forecastModel')
		})
	},

	initializeSeriePeriods: function (range) {
		// Initialize serie periods, starting with the current period and
		// working backwards
		let lastPeriodDelta = { 'scorecard-item': {}, initiative: {} }
		this.getSerieFeatures()
			.filter(s => s.domainType() != 'dataset')
			.forEach(s => {
				let lastDelta = lastPeriodDelta[s.domainType()][s.domainObjectId()]
				let nextDelta = 0
				if (!_.isNil(lastDelta)) {
					nextDelta = lastDelta - 1
				}

				lastPeriodDelta[s.domainType()][s.domainObjectId()] = nextDelta

				s.periods.push(new ChartDesignerSeriePeriod({
					breakdownRangeId: range.get('id'),
					breakdownRangeTempId: range.get('tempId'),
					periodDelta: nextDelta
				}))
			})
	},

	/**
	 * Returns the IDs and visibleLinkedFieldIds of any dataset fields that are
	 * used in one of this chart's series or breakdowns
	 *
	 * @returns {{ fieldIds: Number[], visibleLinkedFieldIds: Number[] }}
	 */
	usedDatasetFieldIds: function () {
		let usedFieldIds = []
		let usedVLFIds = []

		this.getSerieFeatures()
			.filter(f => {
				if (f.domainType() !== 'dataset') {
					return false
				}

				let agg = f.get('source').aggregation

				return agg.dataSourceType === 'basic' && !Dataset.isTotal(agg.fieldId)
			})
			.forEach(f => {
				let agg = f.get('source').aggregation
				usedFieldIds.push(agg.fieldId)
				if (agg.visibleLinkedFieldId) {
					usedVLFIds.push(agg.visibleLinkedFieldId)
				}
			})

		this.breakdowns
			.filter(breakdown => !!breakdown.get('datasetFieldId'))
			.forEach(breakdown => {
				usedFieldIds.push(breakdown.get('datasetFieldId'))

				if (breakdown.get('datasetVisibleLinkedFieldId')) {
					usedVLFIds.push(breakdown.get('datasetVisibleLinkedFieldId'))
				}
			})

		return {
			fieldIds: _.uniq(usedFieldIds),
			visibleLinkedFieldIds: _.uniq(usedVLFIds)
		}
	},

	/**
	 * Filters and returns the chart designer's features
	 *
	 * @param {String} opts.type A feature type (enum string of
	 * ChartDesignerFeatureType) to filter the features by.
	 * @param {String} opts.axis An axis (right or left) to filter the features
	 * by.
	 */
	getFeatures(opts={}) {
		let features = opts.axis ?
			this.featuresByAxis(opts.axis) : [...this.features.models]
		if (_.isEmpty(features)) {
			return []
		}
		features.forEach(f => !_.isNil(f.children) && features.push(...f.children.models))
		return opts.type ? features.filter(feature => feature.get('type') === opts.type) : features
	},

	/**
	 * @returns {ChartDesignerSerie[]} excludes features that are not data serie
	 */
	getSerieFeatures() {
		return this.getFeatures({ type: 'serie' })
	},

	/**
	 * For chart designers with a dataset field, returns the "starting dataset".
	 * Otherwise, returns null
	 *
	 * @returns {Dataset}
	 */
	startingDataset: function () {
		let datasetSerie = this.getSerieFeatures().find(f =>
			f.get('source').domainType === 'dataset' &&
			f.get('source').aggregation.startingDatasetId)
		if (!datasetSerie) {
			return
		}

		let datasetId = datasetSerie.get('source').aggregation.startingDatasetId
		return datasetId ? this.data.datasetModel(datasetId) : null
	},

	datasetGeographicFieldId: function() {
		const feature = this.getSerieFeatures()[0]
		return feature?.get('datasetRollupFieldId')
	},

	/**
	 * returns the associated geographic field for a map chart's only feature
	 */
	datasetGeographicField: function() {
		const dataset = this.startingDataset()
		const geographicFieldId = this.datasetGeographicFieldId()
		return dataset && geographicFieldId ?
			dataset.getUnderlyingField(geographicFieldId) :
			null
	},

	/**
	 * Get the starting dataset or the dataset of the first aggregation of a
	 * calculated dataset source if there is only 1 aggregation
	 * @return {Dataset}
	 */
	getDatasetDatasource: function() {
		const datasetIds = this.getAllDatasetIds()
		if (datasetIds.length == 1) {
			// If we only have a single dataset referenced among all of our series
			// use that
			return this.data.datasetModel(datasetIds[0])
		}

		const datasetSerie = this.getSerieFeatures().find(f =>
			f.get('source').domainType === 'dataset')
		const source = datasetSerie.get('source')
		const agg = source.aggregation
		const aggregations = agg.aggregations
		if (agg.dataSourceType === 'calculated' && aggregations && aggregations.length > 1) {
			// #45458 when we have a calculated dataset with multiple dataset references
			// in the equation, we need to treat this serie like it has no starting
			// dataset
			return null
		}

		return this.getDatasetForDatasetSeries(datasetSerie)
	},

	/**
	 * Gets a dataset for the given datasetSerie. If the series is calculated,
	 * then gets the dataset of the first aggregation (equation part).
	 * @param datasetSerie
	 * @return {Dataset}
	 */
	getDatasetForDatasetSeries: function(datasetSerie) {
		const source = datasetSerie.get('source')
		const agg = source.aggregation
		const aggregations = agg.aggregations
		let datasetId = agg.startingDatasetId ?? agg.datasetId
		// #61707 if there's only one aggregation, then we want to use that instead
		// of the starting dataset
		if (agg.dataSourceType === 'calculated' && aggregations.length === 1) {
			datasetId = aggregations[0].datasetId
		}
		return this.data.datasetModel(datasetId)
	},

	isEditable: function() {
		// Just use report permissions for now, pending #33085
		return permissions.reports(this.get('organizationId')) && permissions.isEditableIfReplicated(this)
	},

	/**
	 * Returns true if there is at least one chart serie using a scorecard item
	 * for its data source
	 */
	hasScorecardItemSources: function () {
		return this.hasSourceWithDomainType('scorecard-item')
	},

	/**
	 * Returns true if there is at least one chart serie using a dataset
	 * for its data source
	 */
	hasDatasetSources: function () {
		return this.hasSourceWithDomainType('dataset')
	},

	hasMultipleDatasetSources: function() {
		return this.getAllDatasetIds().length > 1
	},

	usesCalculatedDataSources: function() {
		return _.some(this.getSerieFeatures(), s => {
			const source = s.get('source')
			if (source.domainType !== 'dataset') return false
			if (source.aggregation.dataSourceType != 'calculated'
				&& !source.aggregation.calculated) return false

			return true
		})
	},

	/**
	 * Returns true if there is at least one chart serie using an initiative
	 * for its data source
	 */
	hasInitiativeSources: function () {
		return this.hasSourceWithDomainType('initiative')
	},

	/**
	 * Returns true if there is at least one chart serie using a scorecard item
	 * for its data source, and also does not have any chart series using either
	 * a dataset or initiative item for a datasource
	 * (#47813 Disable goal line analytic for initiative/dataset charts)
	 */
	hasOnlyScorecardItemSources: function () {
		return this.hasScorecardItemSources() &&
			!this.hasInitiativeSources() && !this.hasDatasetSources()
	},

	hasSourceWithDomainType: function (domainType) {
		// analytics may not have a source
		return !!this.getFeatures().find(f =>
			_.get(unwrapModel(f), 'source.domainType') === domainType)
	},

	supportsScorePointColor: function() {
		const isPolar = _.some(this.getSerieFeatures(), s =>
			unwrapModel(s).plotType === 'polar')
		return (this.hasScorecardItemSources() || this.hasInitiativeSources())
			&& !isPolar
	},

	isRepeating: function () {
		return _.get(this.breakdowns, 'length', 0) !== 0
	},

	hasBarSeries: function({axis}) {
		return this.barSeriesCount({axis}) > 0
	},

	barSeriesCount: function({axis}) {
		return _.filter(
			this.getFeatures({
				type: 'serie',
				axis
			}), f => f.get('plotType') === 'bar').length
	},

	/**
	 * Adaptation of Backbone.Model.toJSON() that leaves out attributes that
	 * don't need to be sent to the server, like error messages and valueSuggestions
	 */
	toPostJSON: function () {
		let c = _.omit(this.toJSON(), 'breakdowns', 'features', 'error')
		c.breakdowns = this.breakdowns.map(b => b.toPostJSON())
		c.features = this.features.map(f => f.toPostJSON())
		return c
	},

	_hasPeriodDeltas: function(startDelta, endDelta) {
		return !_.isNil(startDelta) || !_.isNil(endDelta)
	},

	_hasRelativeDateFilter: function(aggregation) {
		let hasRelativeFilter = _.some(aggregation?.filters, f =>
			this._hasPeriodDeltas(f.periodDelta1, f.periodDelta2))
		let hasRelativeDenominatorFilter = _.some(aggregation?.denominatorFilters, f =>
			this._hasPeriodDeltas(f.periodDelta1, f.periodDelta2))
		let hasRelativeAggregation = _.some(aggregation?.aggregations, a =>
			this._hasRelativeDateFilter(a))
		return hasRelativeFilter || hasRelativeDenominatorFilter || hasRelativeAggregation
	},

	_hasRelativePeriods: function(serie) {
		const periods = serie.get('periods')
		if (periods && periods.length) {
			if (_.some(periods, p => !_.isNil(p.periodDelta))) {
				// Check for any scorecard serie that has a relative
				// period set
				return true
			}
		}
		return false
	},

	_hasRelativeDateBreakdown: function() {
		const breakdowns = this.breakdowns
		if (!breakdowns || !breakdowns.length) return false

		return _.some(breakdowns.toArray(), breakdown => {
			const ranges = breakdown.get('ranges')
			if (ranges) {
				// Find any breakdowns that are using a relative range
				return _.some(ranges, r =>
					this._hasPeriodDeltas(r.startPeriodDelta, r.endPeriodDelta))
			}
			return false
		})
	},

	hasGroupedRangesBreakdown: function() {
		return _.some(this.breakdowns.toArray(),
			b => b.isCalendarPeriodGroupBreakdown())
	},

	/**
	 * #40194 Determines if the period selector (next to the edit tab) should be visible
	 *
	 * @return {Boolean} true to display the period selector; false to hide it
	 */
	showPeriodSelector: function() {
		const features = this.getFeatures({ type: 'serie' })

		if (features) {
			// Check if any series are using relative dates
			for (const feature of features) {
				const source = feature.get('source')
				switch (source.domainType) {
					case 'dataset':
						if (this._hasRelativeDateFilter(source?.aggregation) ||
							// #55003
							this._hasRelativePeriods(feature)) return true
						break
					case 'scorecard-item':
						if (this._hasRelativePeriods(feature)) {
							return true
						} else if (this.hasGroupedRangesBreakdown()) {
							// If we are using a grouped range and we don't have a
							// serie with a relative period set, we should hide
							// the period selector
							return false
						}
						break
				}
			}
		}

		// Check for any breakdowns using relative periods
		if (this._hasRelativeDateBreakdown()) return true

		return false
	},

	/**
	 * #41174 Returns true if this chart displays a single period's data
	 */
	isSinglePeriod() {
		if (!this.breakdowns.length) return false

		// We need to track that we did indeed find a relative breakdown
		// If we get all the way through the for-loop and this flag is
		// set, that means we have a single period relative calendar breakdown.
		// If this doesn't get set, that means we have no relative
		// calendar breakdowns, relative or otherwise
		let hasRelativePeriodBreakdown = false

		for (const breakdown of this.breakdowns.toArray()) {
			const { ranges } = unwrapModel(breakdown)
			if (breakdown.isCalendarPeriodGroupBreakdown() ||
				breakdown.isCalendarPeriodStandardBreakdown()) {
				const hasMultiPeriodOrDateRange = _.some(ranges, r => {
					// Check for static dates
					if (_.isNil(r.startPeriodDelta) && _.isNil(r.endPeriodDelta)) return false

					hasRelativePeriodBreakdown = true

					// Check that the relative range is more than 1
					return r.startPeriodDelta != r.endPeriodDelta
				})
				if (hasMultiPeriodOrDateRange) return false
			}
		}

		return hasRelativePeriodBreakdown
	},

	isShowingSingleChart: function() {
		const singlePeriod = this.isSinglePeriod()
		const isRepeating = this.isRepeating()

		const existMultiChartBreakdowns = _(this.get('breakdowns') || [])
			.map(it => _.defaultTo(it.multipleCharts, true))
			.some()

		const onlyOneSpecificValueRange = _(this.get('breakdowns'))
			.flatMap(it => it.ranges)
			.filter(it => it.type === 'dataset-value')
			.value()
			.length === 1

		return singlePeriod || !isRepeating || !existMultiChartBreakdowns ||
			onlyOneSpecificValueRange
	},

	isHierarchical: function() {
		return _.includes(['tree-map', 'filled-map', 'spike-map',
			'bubble-map'],
			this.get('plotType'))
	},

	hasBreakdowns: function() {
		return this.breakdowns.length > 0
	},

	getEnabledMapLevels: function() {
		const enabledMapLevels = _.filter(_.keys(enums.geographicMapLevel), ml =>
			this.get(`${_.camelCase(ml)}Enabled`))
		return enabledMapLevels
	},

	/**
	 * Returns the id of the node that we are viewing (i.e. if we are viewing 50
	 * states, this will return the id of the country)
	 */
	getGeographicNodeId: function() {
		const serie = _.get(this.data.plots()[0], 'series[0]')
		if (_.isNil(serie)) return null

		const rootId = geographicTreeRootId({ designer: this })
		return serie?.drilldownId || this.get('startingViewTreeId') ||
			rootId
	},

	getConditionalColoringModel: function() {
		const plots = this.data.plots()
		if (_.isEmpty(plots)) return

		const bounds = geographicMapBounds({
			designer: this,
			points: plots[0].series[0].points
		})
		if (_.isNil(bounds)) return

		const thresholdColoring = this.get('colors')[0].thresholdColoring
		return new ThresholdColorModel(_.extend(bounds, thresholdColoring))
	},

	/**
	 * Updates the 'error' property for this designer and its child models
	 * (features, breakdowns, etc) based on the provided designer (presumably
	 * from a server validation call)
	 *
	 * @param {ChartDesigner|Object} updatedDesigner
	 */
	applyErrors: function (updatedDesigner) {
		updatedDesigner = unwrapModel(updatedDesigner)

		updateError(this, updatedDesigner)

		this.features.forEach((featureModel, i) => {
			let updatedFeature = updatedDesigner.features[i]
			updateError(featureModel, updatedFeature)

			featureModel.children.forEach((childModel, i) => {
				let updatedChild = updatedFeature.children[i]
				updateError(childModel, updatedChild)
			})
		})

		this.breakdowns.forEach((breakdownModel, i) => {
			let updatedBreakdown = updatedDesigner.breakdowns[i]
			updateError(breakdownModel, updatedBreakdown)
		})
	},

	/**
	 * Returns true if the chart or any of its components (features, breakdowns, etc)
	 * has an error
	 */
	hasError: function () {
		return this.errors().length > 0
	},

	errors () {
		// Similar logic in ChartDesinger.kt; if this changes, update there, too
		let errors = []
		if (this.get('error')) {
			errors.push(this.get('error'))
		}

		return errors.concat(this.breakdowns.toArray().flatMap(b => b.errors()))
			.concat(this.features.toArray().flatMap(f => f.errors()))
	},

	drilldownFieldAndId () {
		const plotType = this.get('plotType')
		if (plotType !== 'tree-map' &&
			plotType !== 'filled-map' &&
			plotType !== 'spike-map' &&
			plotType !== 'bubble-map') {
			return null
		}

		let fieldId = this.features.first().get('datasetRollupFieldId')
		let drilldownId = _.get(this.data.currentDrilldown(), 'drilldownId')

		return { fieldId, drilldownId }
	}
})

function updateError (model, updatedObject) {
	if (updatedObject.error) {
		model.set('error', updatedObject.error)
	} else if (model.get('error')) {
		// #41154 only unset if one exists because this will change/dirty the form
		model.unset('error')
	}
}

const FeatureCollection = Backbone.Collection.extend({
	sync: mockSync,
	model: function ModelFactory(attrs, options) {
		const FeatureModel = featureMetadata[attrs.type].model || ChartDesignerSerie
		return new FeatureModel(attrs, options)
	},

	/**
	 * Given either a "real" persisted ID or a tempId for a serie that hasn't
	 * yet been saved, returns the corresponding ChartDesignerSerie model
	 */
	findByIds: function ({ id, tempId }) {
		return findByIds({ id, tempId, collection: this })
	},

	/**
	 * Returns the series, sorted by axis then sortOrder
	 *
	 * @returns {ChartDesignerSerie[]}
	 */
	ordered: function () {
		let series = this.toArray()
		series.sort((f1, f2) => {
			if (f1.get('axis') !== f2.get('axis')) {
				return f1.get('axis')=== 'left' ? -1 : 1
			}

			return f1.get('sortOrder') - f2.get('sortOrder')
		})

		return series
	},

	/**
	 * Returns a single list with all parent features and their child features
	 * @returns {ChartDesignerFeature[]}
	 */
	featuresAndChildren: function () {
		return _.flatMap(this.toArray(), f => {
			let features = [f]
			f.children.forEach(c => features.push(c))
			return features
		})
	}
})

const BreakdownCollection = Backbone.Collection.extend({
	sync: mockSync,
	model: ChartDesignerBreakdown,

	/**
	 * Given either a "real" persisted ID or a tempId for a breakdown that hasn't
	 * yet been saved, returns the corresponding ChartDesignerBreakdown model
	 */
	findByIds: function ({ id, tempId }) {
		return findByIds({ id, tempId, collection: this })
	},

	findBySortOrder (sortOrder) {
		return this.find(b => b.get('sortOrder') == sortOrder)
	}
})

/**
 * For a list of series periods, returns the latest absolute calendar period ID
 *
 * @param {ChartDesignerSeriePeriod[]} seriesPeriods
 *
 * @returns {Int} ID of the most recent period ID
 */
function latestPeriod (seriesPeriods) {
	return _(seriesPeriods)
		.map(p => calendars.periodsById[p.get('periodId')])
		.maxBy(p => p.start.toDate())
}

function tempChartDesignerId () {
	return _.uniqueId('c')
}

/**
 * returns the root id for the corresponding geographic field or the
 * subdivisions id by default if there is no field given a designer or field
 */
function geographicTreeRootId({ designer, field }) {
	const geographicField = field || designer?.datasetGeographicField()

	return geographicField?.dataType === POSTAL_CODE ?
		constants.POSTAL_CODES_ROLLUP_ID : constants.SUBDIVISIONS_ROLLUP_ID
}

function geographicMapBounds({ designer, points }) {
	let rawValues
	if (_.size(points) == 1) {
		// in the event there is only one point, it means we are looking at
		// a single parent node with no children instead of the children of
		// the node without the parent. we want to include the value
		// of that parent as it will be displayed on the map
		rawValues = [points[0].raw]
	} else {
		const parentId = designer.getGeographicNodeId()
		rawValues = _(points)
			.filter(ti => ti.rangeId != parentId)
			.map(d => d.raw)
			.filter(v => !_.isNil(v))
			.value()
	}
	if (_.size(rawValues) == 0) return null

	return { min: _.min(rawValues), max: _.max(rawValues) }
}

/**
 * Adjusts the manual y axis min max settings. Changes the values from percent
 * to number or vice versa if the axis data type has changed
 */
const adjustYAxisSettings = (oldInfo, newInfo, chart) => {
	const adjustAxis = (axis, percentToNumber) => {
		const adjustment = percentToNumber ? 2 : -2
		adjustAxisSetting(axis, 'Min', adjustment)
		adjustAxisSetting(axis, 'Max', adjustment)
	}
	const adjustAxisSetting = (axis, setting, adjustment) => {
		const property = `${axis}Axis${setting}`
		const oldValue = chart.get(property)
		if (!_.isNil(oldValue)) {
			const newValue = moveDecimal(oldValue, adjustment)
			chart.set(property, newValue)
		}
	}

	_.each(_.keys(newInfo), axis => {
		const oldDataType = oldInfo[axis]?.dataType
		const newDataType = newInfo[axis]?.dataType
		if (oldDataType === PERCENT && _.includes([NUMBER, CURRENCY], newDataType)) adjustAxis(
			axis, true)
		else if (_.includes([NUMBER, CURRENCY], oldDataType) && newDataType === PERCENT) adjustAxis(
			axis, false)
	})
}

export default ChartDesigner
export { tempChartDesignerId }
export { geographicTreeRootId }
export { geographicMapBounds }
export { adjustYAxisSettings }

ChartDesigner.__filename__ = 'charts/model/chart-designer.js'
